import {
  type DatabaseAifcAnalyticsSnapshot,
  buildDatabaseAifcAnalyticsSnapshot,
  getRetainedElementCount,
} from './database-aifc-analytics-snapshot';

const ACTIONED_DELAY_MS = 10_000;
const REMAINING_ELEMENTS_PERCENT = 0.5;

type PendingEvaluation = {
  contentId: string;
  operationType: 'create' | 'edit';
  baselineElementCount: number;
  baselineSnapshot: DatabaseAifcAnalyticsSnapshot;
  currentSnapshotBuilder: () => DatabaseAifcAnalyticsSnapshot;
  timeoutId: ReturnType<typeof setTimeout>;
  singleInstrumentationID: string;
};

type FireActionedEvent = (params: {
  contentId: string;
  operationType: 'create' | 'edit';
  totalElements: number;
  remainingElements: number;
  singleInstrumentationID: string;
}) => Promise<void>;

type ScheduleEvaluationParams = {
  contentId: string;
  operationType: 'create' | 'edit';
  snapshotBuilder?: () => DatabaseAifcAnalyticsSnapshot;
  singleInstrumentationID: string;
};

export class DatabaseAifcAnalyticsSystem {
  private pendingEvaluation: PendingEvaluation | null = null;

  constructor(
    private readonly fireActionedEvent: FireActionedEvent,
    private readonly snapshotBuilder: typeof buildDatabaseAifcAnalyticsSnapshot = buildDatabaseAifcAnalyticsSnapshot
  ) {}

  public scheduleEvaluation({
    contentId,
    operationType,
    snapshotBuilder,
    singleInstrumentationID,
  }: ScheduleEvaluationParams): void {
    this.cancelPendingEvaluation();

    const currentSnapshotBuilder =
      snapshotBuilder ?? (() => this.snapshotBuilder(contentId));
    const baselineSnapshot = currentSnapshotBuilder();
    const baselineElementCount = baselineSnapshot.elementKeys.length;

    if (baselineElementCount === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void this.evaluatePending();
    }, ACTIONED_DELAY_MS);

    this.pendingEvaluation = {
      contentId,
      operationType,
      baselineElementCount,
      baselineSnapshot,
      currentSnapshotBuilder,
      timeoutId,
      singleInstrumentationID,
    };
  }

  public cancelPendingEvaluation(): void {
    if (this.pendingEvaluation !== null) {
      clearTimeout(this.pendingEvaluation.timeoutId);
      this.pendingEvaluation = null;
    }
  }

  private async evaluatePending(): Promise<void> {
    if (this.pendingEvaluation === null) {
      return;
    }

    const {
      contentId,
      operationType,
      baselineElementCount,
      baselineSnapshot,
      currentSnapshotBuilder,
      singleInstrumentationID,
    } = this.pendingEvaluation;
    this.pendingEvaluation = null;

    const currentSnapshot = currentSnapshotBuilder();
    const remainingElements = getRetainedElementCount(baselineSnapshot, currentSnapshot);
    const remainingRatio = remainingElements / baselineElementCount;

    if (remainingRatio > REMAINING_ELEMENTS_PERCENT) {
      await this.fireActionedEvent({
        contentId,
        operationType,
        totalElements: baselineElementCount,
        remainingElements,
        singleInstrumentationID,
      });
    }
  }
}
